import type { User } from './user';

export interface AuthUser extends User {
  verified?: boolean;
  locale?: Locale;
  /** @deprecated Use splitLocale(authUser.locale) instead */
  culture?: string;
  emailNotifications?: boolean;
  emailMarketingNotifications?: boolean;
  gdprConsent?: boolean | null;
  phone?: string;
  telegramUsername?: string;
  canCreateOrg?: boolean;
  notes?: string;
}

export const languages = ['en', 'de', 'ru'] as const;
export type Language = (typeof languages)[number];

/**
 * Endonyms — each UI Language rendered in its own script — so a User can
 * find their language without reading a foreign one. Single source of truth
 * shared by every language picker (account form, nav drawer, booking wizard).
 */
export const langLabels: Record<Language, string> = {
  en: 'English',
  de: 'Deutsch',
  ru: 'Русский',
};

export const presetCultures = [
  'DE',
  'AT',
  'CH',
  'GB',
  'US',
  'FR',
  'ES',
  'IT',
  'NL',
  'BE',
  'PL',
  'RU',
] as const;
export type PresetCulture = (typeof presetCultures)[number];

export type Locale = `${Language}-${PresetCulture}` | `${Language}-${string}`;

export interface SplitLocale {
  language: Language;
  /**
   * The raw uppercased region from the locale string. This is whatever the
   * source actually contained (e.g. `XX` from `en-XX`), so it is a plain
   * string rather than a {@link PresetCulture} — `Locale` already permits any
   * region via its `${Language}-${string}` arm, so narrowing here buys nothing.
   */
  culture: string;
}

export function splitLocale(locale: string): SplitLocale {
  const parts = locale.split('-');
  const rawLang = parts[0]?.toLowerCase() ?? '';
  const culture = parts[1]?.toUpperCase() ?? 'DE';

  const language: Language = (languages as readonly string[]).includes(rawLang)
    ? (rawLang as Language)
    : 'de';

  return { language, culture };
}

export function resolveLocale(locale: string | undefined): Locale {
  const raw = locale || 'de-DE';
  const { language, culture } = splitLocale(raw);
  const resolved: Locale = `${language}-${culture}`;
  return resolved;
}

/**
 * The locales the product actually supports for formatting (Angular LOCALE_ID,
 * pipes, email dates). Russian collapses to ru-RU, any English variant to
 * en-GB, and everything else to de-DE, so en-US formatting (which we never
 * want) can't leak through.
 */
export type SupportedLocale = 'en-GB' | 'de-DE' | 'ru-RU';

export function toSupportedLocale(
  locale: string | null | undefined,
): SupportedLocale {
  const language = splitLocale(locale ?? '').language;
  return language === 'ru' ? 'ru-RU' : language === 'en' ? 'en-GB' : 'de-DE';
}

export type OrgRole = {
  id: string;
  role: UserRole;
  organisation: string;
  shop: string;
  /** When empty, the user has access to all shops */
  shops: string[];
};

export interface Tokens {
  access_token: string;
  refresh_token: string;
  expires_in: number;
}

export interface AuthResponse {
  token: string;
  tokens: Tokens;
  user: AuthUser;
}

export type UserRole = (typeof roles)[number];
export const roles = [
  'owner',
  'admin',
  'editor',
  'viewer',
  'customer',
] as const;

export interface RegisterOptions {
  email: string;
  firstname: string;
  lastname: string;
  displayName?: string; // Optional display name
  password?: string;
  locale?: string; // Can be simple ("en") or culture-aware ("en-US") - will be parsed by backend
  emailNotifications?: boolean;
  emailMarketingNotifications?: boolean;
  organisationId?: string;
  shopId?: string; // Made optional for organization-level staff
  role?: UserRole;
  phone?: string;
  notes?: string;
}

export type StaffUser = {
  auto_topup_enabled: boolean;
  auto_topup_payment_method: string;
  avatar: string;
  canCreateOrg: boolean;
  collectionId: string;
  collectionName: string;
  created: string;
  email: string;
  emailMarketingNotifications: boolean;
  emailNotifications: boolean;
  emailVerificationToken: string;
  id: string;
  locale: string; // Combined format (e.g., "en" or "en-US") - parsed from locale+culture fields
  firstname?: string;
  lastname?: string;
  displayName?: string;
  profileImageUrl?: string;
  phone: string;
  resetPasswordToken: string;
  stripe_customer_id: string;
  subscriptionActive: boolean;
  subscriptionCancelledAtPeriodEnd: boolean;
  subscriptionCurrentPeriodEnd: string;
  telegramId: string;
  telegramUsername: string;
  tokens_balance: number;
  tokens_balance_extra: number;
  tokens_used_this_month: number;
  updated: string;
  verified: boolean;
  notes?: string;
};

export type AuthMethod = (typeof authMethods)[number];

const authMethods = ['otp', 'password', 'passkey', 'google'] as const;

/**
 * Which screen initiated a Google sign-in. The backend's deep sign-in module
 * keys its policy off this (ADR 0007: the intent travels as a POST parameter,
 * not a redirect `state` round-trip).
 */
export type GoogleSignInIntent = 'staff-login' | 'owner-signup' | 'customer';

/** Deep-module code for "no staff account for this Google email" (e00297). */
export const STAFF_GOOGLE_NOT_FOUND_CODE = 'e00297';
